import {
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabelProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { SxProps } from '@mui/system';
import LoadingSpinner from 'components/loading-spinner';
import React, { useState } from 'react';
import { Order, getComparator, stableSort } from 'utils/list-sort';
import HeadCell from './head-cell';
import ListCell from './list-cell';

const PREFIX = 'Table';

/** Used to limit recursion depth in NestedKeyOf to prevent TypeScript instantiation limits. */
type Prev = [never, 0, 1, 2, 3, 4, 5];

/**
 * Recursively produces a union of all dot-notation string paths into T up to a given depth.
 * e.g. NestedKeyOf<{ a: { b: string } }> = 'a' | 'a.b'
 * Arrays are not traversed — only their key is included.
 */
export type NestedKeyOf<T, D extends number = 2> = [D] extends [never]
  ? never
  : T extends object
    ? {
        [K in keyof T & string]: NonNullable<T[K]> extends any[]
          ? K
          : NonNullable<T[K]> extends object
            ? K | `${K}.${NestedKeyOf<NonNullable<T[K]>, Prev[D]>}`
            : K;
      }[keyof T & string]
    : never;

export interface HeadCell<T = any> {
  /**
   * The key or dot-notation path to the value used for sorting (e.g. 'name' or 'generation.displayName').
   * When T is provided, autocomplete will suggest valid paths. Non-data columns (e.g. 'actions') are also accepted.
   */
  id: NestedKeyOf<T> | (string & {});
  colSpan?: number;
  label: string | React.ReactNode;
  align?: 'left' | 'right' | 'center';
  fullWidth?: boolean;
  autoWidth?: boolean;
  sx?: SxProps<Theme> | undefined;
  hide?: boolean;
  sticky?: boolean;
  background?: string;
  /**
   * An optional info text that will be shown as a tooltip on the column header. Can be used to provide additional context about the column.
   */
  info?: string;
  sortLabelProps?: TableSortLabelProps;
  disableSort?: boolean;
  /**
   * A custom comparator function for sorting the column. If not provided, a default comparator will be used that sorts based on the value at the specified id path.
   * @param a The first item to compare.
   * @param b The second item to compare.
   * @returns A negative number if a should come before b, a positive number if a should come after b, or 0 if they are considered equal.
   */
  comparator?: (a: T, b: T) => number;
}

type ListProps<T = any> = {
  noResult?: {
    show: boolean;
    text: string | React.ReactNode;
  };
  children: React.ReactNode;
  options?: HeadCell<T>[];
  isLoading?: boolean;
  orderBy?: NestedKeyOf<T> | (string & {});
  rows?: T[];
  dark?: boolean;
  className?: string | undefined;
  footer?: React.ReactNode;
  sx?: SxProps<Theme> | undefined;
  sort?: (value: T[]) => void;
  isDataGrid?: boolean;
  isMobile?: boolean;
};

function List<T = any>({
  options,
  className,
  children,
  sx,
  dark = false,
  rows,
  noResult,
  isLoading,
  sort,
  orderBy: defaultOrderBy,
  footer,
  isMobile,
}: ListProps<T>) {
  const theme = useTheme();
  const isInternalMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<string>(defaultOrderBy ?? '');

  const backgroundClass = dark ? 'Mui-backgroundDark' : 'Mui-backgroundLight';

  const handleRequestSort = (property: string) => {
    if (sort && rows) {
      const isDesc = orderBy === property && order === 'desc';
      const newOrder = isDesc ? 'asc' : 'desc';
      setOrder(newOrder);
      setOrderBy(property);

      const column = options?.find((col) => col.id === property);
      const comparator = column?.comparator
        ? (a: T, b: T) => (newOrder === 'desc' ? -column.comparator!(a, b) : column.comparator!(a, b))
        : getComparator<T>(newOrder, property);

      sort([...stableSort(rows, comparator)]);
    }
  };
  // TODO: Do not use overflowX, make different view for mobile

  return isLoading ? (
    <TableContainer sx={sx}>
      <Table
        className={backgroundClass}
        sx={{
          marginBottom: 2,
        }}>
        <TableBody>
          <TableRow>
            <ListCell
              sx={{
                backgroundColor: 'white',
                borderRadius: 2.5,
              }}>
              <LoadingSpinner />
            </ListCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  ) : noResult?.show ? (
    <TableContainer sx={sx}>
      <Table
        className={backgroundClass}
        sx={{
          marginBottom: 2,
        }}>
        <TableBody>
          <TableRow>
            <ListCell
              sx={{
                backgroundColor: 'white',
                borderRadius: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Typography fontWeight={500} textAlign={'center'} color={theme.palette.darkBlue.main}>
                {noResult.text}
              </Typography>
            </ListCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  ) : isMobile || isInternalMobile ? (
    <div className={className}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          let props = { isMobile: true, options: options };
          return React.cloneElement(child, props);
        }
      })}
    </div>
  ) : (
    <TableContainer sx={sx}>
      <Table className={backgroundClass}>
        {options ? (
          <TableHead>
            <TableRow>
              {options.map((headCell, idx) => (
                <HeadCell
                  key={idx}
                  headCell={headCell}
                  handleRequestSort={handleRequestSort}
                  sort={sort}
                  order={order}
                  orderBy={orderBy}
                />
              ))}
            </TableRow>
          </TableHead>
        ) : null}
        <TableBody>{children}</TableBody>
        {!!footer && <TableFooter>{footer}</TableFooter>}
      </Table>
    </TableContainer>
  );
}

export default List;
